import {path, pipe, prop, find} from 'ramda';
import {createSelector} from 'reselect';
import namespace from './namespace';
import {selectors as lifecycleSelectors} from 'fragments/lifecycle';
import {
	formatUrlQuery,
	formatFetchableCalendarQuery,
	isSalesmanUser,
	formatCalendarResourceAddFormInput,
	formatCalendarResourceEditFormInput,
	isReadOnlyUser,
	formatCalendarResourceDateFormInput,
	formatEncounterAddFormInput,
	showRequestsForUser,
	formatReminderAddFormInput,
	checkTeamCalendarReminderVisibility,
	formatCalendarEventEditFormInput,
	formatCalendarEventAddFormInput,
} from './utils';
import {filterTeamTempAreas} from 'utils/areas';
import * as commonSelectors from 'modules/common/selectors';
import {
	calendarCreateCalendarEventForAnyUser,
	userCanAddFreeTimesForSalesman,
	userCanCancelVisit,
	canViewCalendarResourceSource as canViewCalendarReservationSourcePerm,
	canGetVisitAi as canGetVisitAiPerm,
	canUseVisitAi as canUseVisitAiPerm,
	canManageAiPrompts as canManageAiPromptsPerm,
} from 'utils/perms';
import createCallReminderSelectors from 'fragments/callReminder/selectors';

const root = path(namespace);

export const {seq} = lifecycleSelectors(namespace);

export const initialized = pipe(root, prop('initialized'));

export const processing = pipe(root, prop('processing'));

export const calResLoading = pipe(root, prop('calResLoading'));

export const calendarQuery = pipe(root, prop('calendarQuery'));

export const urlQuery = createSelector([calendarQuery], calendarQuery => {
	return formatUrlQuery({calendarQuery});
});

export const calendarQueryFetchable = createSelector([calendarQuery], calendarQuery => {
	return formatFetchableCalendarQuery({calendarQuery});
});

export const teamId = createSelector(
	[calendarQuery],
	calendarQuery => calendarQuery.teamId,
);

export const weekSample = createSelector(
	[calendarQuery],
	calendarQuery => calendarQuery.weekSample,
);

export const onlyRequests = createSelector(
	[calendarQuery],
	calendarQuery => calendarQuery.teamId === 'requests',
);

export const hourItems = pipe(root, prop('hourItems'));

export const hourlessItems = pipe(root, prop('hourlessItems'));

export const itemsSource = pipe(root, prop('itemsSource'));

export const teams = pipe(root, prop('teams'));

export const activeOrganizationTeams = pipe(root, prop('activeOrganizationTeams'));

export const team = createSelector(
	[teamId, activeOrganizationTeams],
	(teamId, activeOrganizationTeams) =>
		find(team => team.id === teamId, activeOrganizationTeams),
);

export const itemsViewerOpen = pipe(root, prop('itemsViewerOpen'));

export const itemsCreatorOpen = pipe(root, prop('itemsCreatorOpen'));

export const slotSelection = pipe(root, prop('slotSelection'));

// PERM: ignore-visible-for-salesman
export const isSalesman = createSelector([commonSelectors.user], user =>
	isSalesmanUser(user),
);

// PERM: team-calendar-items-manage
export const isReadOnly = createSelector([commonSelectors.user], user =>
	isReadOnlyUser(user),
);

// PERM: team-calendar-show-requests
export const showRequests = createSelector([commonSelectors.user], user =>
	showRequestsForUser(user),
);

export const calendarResourceAddFormInitValues = createSelector([team], team =>
	formatCalendarResourceAddFormInput(team),
);

export const canViewCalendarResourceSource = createSelector(
	[commonSelectors.user],
	user => canViewCalendarReservationSourcePerm(user),
);

export const selectedItem = pipe(root, prop('selectedItem'));

export const selectedItemLoading = pipe(root, prop('selectedItemLoading'));

export const selectedItemLoadingId = pipe(root, prop('selectedItemLoadingId'));

export const previewableEncounter = pipe(root, prop('previewableEncounter'));

export const calendarResourceEditFormInitValues = createSelector(
	[selectedItem],
	selectedItem => formatCalendarResourceEditFormInput(selectedItem),
);

export const calendarEventEditFormInitValues = createSelector(
	[selectedItem],
	selectedItem => formatCalendarEventEditFormInput(selectedItem),
);

export const areasEditorOpen = pipe(root, prop('areasEditorOpen'));

export const dateEditorOpen = pipe(root, prop('dateEditorOpen'));

export const buildingCalendarResources = pipe(root, prop('buildingCalendarResources'));

export const buildingCalendarResourcesLoading = pipe(
	root,
	prop('buildingCalendarResourcesLoading'),
);

export const calendarBuildingModalOpen = pipe(root, prop('calendarBuildingModalOpen'));

export const building = pipe(root, prop('building'));

export const bonusItemReservationModalOpen = pipe(
	root,
	prop('bonusItemReservationModalOpen'),
);

export const bonusItemBeingReserved = pipe(root, prop('bonusItemBeingReserved'));

export const calendarResourceDateFormInitValues = createSelector(
	[bonusItemBeingReserved],
	bonusItemBeingReserved => formatCalendarResourceDateFormInput(bonusItemBeingReserved),
);

export const canCancelVisit = createSelector(
	[commonSelectors.user, selectedItem],
	(user, calendarResource) =>
		calendarResource && userCanCancelVisit({user, calendarResource}),
);

export const teamAreas = createSelector([team, slotSelection], (team, slotSelection) =>
	team ? filterTeamTempAreas(team.areas, slotSelection.date) : [],
);

export const leadBuilding = pipe(root, prop('leadBuilding'));

export const leadClient = pipe(root, prop('leadClient'));

export const lead = pipe(root, prop('lead'));

export const encounterAddFormInitValues = createSelector(
	[commonSelectors.user, leadBuilding, leadClient, lead],
	(user, building, clientId, lead) =>
		formatEncounterAddFormInput({user, building, clientId, lead}),
);

export const addBuildingModalOpen = pipe(root, prop('addBuildingModalOpen'));

export const mapModalOpen = pipe(root, prop('mapModalOpen'));

export const addBuildingFormInitValues = pipe(root, prop('addBuildingFormInitValues'));

export const buildingToAdd = pipe(root, prop('buildingToAdd'));

export const callReminderBuilding = pipe(root, prop('callReminderBuilding'));

const callReminderSelectors = createCallReminderSelectors({namespace});

export const {hourReminders} = callReminderSelectors;

export const callReminderAddFormInitValues = createSelector(
	[slotSelection],
	slotSelection => formatReminderAddFormInput(slotSelection),
);

export const userTeam = pipe(root, prop('userTeam'));

export const teamCalendarCallRemindersVisible = createSelector(
	[teamId, userTeam],
	(teamId, userTeam) =>
		checkTeamCalendarReminderVisibility({
			teamId,
			userTeam,
		}),
);

export const marketingLeadSources = pipe(root, prop('marketingLeadSources'));

export const products = pipe(root, prop('products'));

export const clientEditorOpen = pipe(root, prop('clientEditorOpen'));

export const lockingResources = pipe(root, prop('lockingResources'));

export const lockFrom = pipe(root, prop('lockFrom'));

export const lockModalOpen = pipe(root, prop('lockModalOpen'));

export const canCreateCalendarEventForAnyUser = createSelector(
	[commonSelectors.user],
	user => calendarCreateCalendarEventForAnyUser(user),
);

export const canAddFreeTimesForSalesman = createSelector([commonSelectors.user], user =>
	userCanAddFreeTimesForSalesman(user),
);

export const canEditCalendarEvent = createSelector(
	[commonSelectors.user, selectedItem],
	(user, item) => {
		return user && item && item?.model === 'CalendarEvent' && item.userId === user.id;
	},
);

export const calendarEventAddFormInitValues = createSelector(
	[slotSelection, commonSelectors.user, team],
	(slotSelection, user, team) =>
		formatCalendarEventAddFormInput(slotSelection, user, team),
);

export const calendarEventTypes = pipe(root, prop('calendarEventTypes'));

export const canGetVisitAi = createSelector([commonSelectors.user], user =>
	canGetVisitAiPerm(user),
);

export const canUseVisitAi = createSelector([commonSelectors.user], user =>
	canUseVisitAiPerm(user),
);

export const canManageAiPrompts = createSelector([commonSelectors.user], user =>
	canManageAiPromptsPerm(user),
);

export const visitAiModalOpen = pipe(root, prop('visitAiModalOpen'));

export const visitAiLoading = pipe(root, prop('visitAiLoading'));

export const visitAiResult = pipe(root, prop('visitAiResult'));

export const visitAiDate = pipe(root, prop('visitAiDate'));

export const visitAiAnalyzing = pipe(root, prop('visitAiAnalyzing'));

const sortByDateFrom = items =>
	[...items].sort((a, b) => {
		const aTime = new Date(a.dateFrom).getTime();
		const bTime = new Date(b.dateFrom).getTime();
		return aTime - bTime;
	});

const toVisitKey = id => (id == null ? null : String(id));

const visitAiDayResources = pipe(root, prop('visitAiDayResources'));

export const visitAiVisitsForDate = createSelector(
	[visitAiDayResources, visitAiResult, visitAiDate],
	(items, result, date) => {
		if (!date || !items.length) return [];
		const dayItems = items.filter(item => {
			if (item.model === 'CalendarEvent' || item.cancelled) return false;
			if (!item.buildingId) return false;
			if (item.salesmanVisitId) return false;
			const d = item.dateFrom instanceof Date ? item.dateFrom : new Date(item.dateFrom);
			return d.toISOString().slice(0, 10) === date;
		});
		const sortedDayItems = sortByDateFrom(dayItems);
		const analysisMap = new Map();
		(result?.data?.visits || []).forEach(v => {
			const key = toVisitKey(v.calendarResourceId);
			if (key) {
				analysisMap.set(key, v);
			}
		});
		return sortedDayItems.map(item => ({
			calendarResource: item,
			analysis: analysisMap.get(toVisitKey(item.id)) || null,
		}));
	},
);

// Read from itemsSource (live calendar state, updated by realtime events) —
// visitAiDayResources is a snapshot fetched once at modal open, so a visit
// acknowledged while the modal is open would not surface here.
export const visitAiAcknowledgedForDate = createSelector(
	[itemsSource, visitAiResult, visitAiDate],
	(items, result, date) => {
		if (!date || !items.length) return [];
		const dayItems = items.filter(item => {
			if (item.model === 'CalendarEvent' || item.cancelled) return false;
			if (!item.buildingId) return false;
			if (!item.salesmanVisitId) return false;
			const d = item.dateFrom instanceof Date ? item.dateFrom : new Date(item.dateFrom);
			return d.toISOString().slice(0, 10) === date;
		});
		const analysisMap = new Map();
		(result?.data?.visits || []).forEach(v => {
			const key = toVisitKey(v.calendarResourceId);
			if (key) analysisMap.set(key, v);
		});
		return sortByDateFrom(dayItems).map(item => ({
			...item,
			analysis: analysisMap.get(toVisitKey(item.id)) || null,
		}));
	},
);
